import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Swal from "sweetalert2";
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { getEventAll } from '../services/dbService'; // Assuming this is your API call
import "./dashboard.css"; // Your custom dashboard CSS

function Dashboard() {
  const [searchParams] = useSearchParams(); // Not used directly in render, but kept if for future use
  const [eventData, setEventData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userName, setUserName] = useState('User');
  const [membershipValidUpto, setMembershipValidUpto] = useState('Unknown');

  // --- Utility Functions ---
  const getDaySuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };
  // const formatReadableDate = (dateStr) => {
  //   try {
  //     if (!dateStr) return 'Invalid Date';
  //     const date = new Date(dateStr);
  //     if (isNaN(date)) return 'Invalid Date'; // Handle invalid date strings
  
  //     const day = date.getDate().toString().padStart(2, '0');
  //     const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
  //     const year = date.getFullYear();
  
  //     return `${month}-${day}-${year}`;
  //   } catch (error) {
  //     console.error("Error formatting date:", error);
  //     return 'Invalid Date';
  //   }
  // };


  const formatReadableDate = (dateStr) => {
    try {
      if (!dateStr) return 'Invalid Date';
      const date = new Date(dateStr);
      if (isNaN(date)) return 'Invalid Date';
  
      const day = date.getUTCDate().toString().padStart(2, '0');
      const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
      const year = date.getUTCFullYear();
  
      return `${month}-${day}-${year}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return 'Invalid Date';
    }
  };
  
  
  const formatReadableDateRange = (startDate, endDate) => {
    try {
      const formattedStart = formatReadableDate(startDate);
      const formattedEnd = formatReadableDate(endDate);
  
      return startDate === endDate ? formattedStart : `${formattedStart} to ${formattedEnd}`;
    } catch (error) {
      console.error("Error formatting date range:", error);
      return 'Invalid Date Range';
    }
  };
  
  // --- Data Fetching ---
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch user info from localStorage
        const storedFirstName = localStorage.getItem('firstname');
        const storedLastName = localStorage.getItem('lastname');
        if (storedFirstName || storedLastName) {
          setUserName(`${storedFirstName || ''} ${storedLastName || ''}`.trim());
        } else {
          setUserName('User');
        }

        const storedValidUpto = localStorage.getItem('validupto');
        setMembershipValidUpto(formatReadableDate(storedValidUpto));

        // Fetch event data
        const response = await getEventAll();
        const events = Array.isArray(response?.data)
          ? response.data.filter((x) => x.registrationallowed === '1')
          : [];
        setEventData(events);

      } catch (err) {
        console.error("Error fetching dashboard data:", err); // Log the full error
        Swal.fire('Error', 'Failed to load dashboard data. Please try again.', 'error');
        setError('Failed to load events and user data.'); // Set specific error message for display
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []); // Empty dependency array means this runs once on component mount

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <Sidebar />

      {/* Loading Spinner */}
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-75 z-50">
          <div className="preloader">
            <div className="lds-ripple">
              <div className="lds-pos"></div>
              <div className="lds-pos"></div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Wrapper */}
      <div id="main-wrapper">
        <div className="page-wrapper d-block">
          <div className="body-wrapper">
            <div className="container-fluid px-4 py-6">

              {/* Welcome Section */}
              <div className="p-4 text-center bg-white shadow-sm mb-4 rounded-lg"> {/* Added rounded corners */}
                <h5 className="text-xl font-semibold text-gray-800">Welcome {userName}!</h5>
                <p className="text-sm text-gray-600">
                  Your membership is valid until: <strong>{membershipValidUpto}</strong>.
                </p>
                <Link to="/renewmembershippaypal"> {/* Use Link for internal navigation */}
                  <button className="btn btn-info btn-sm mt-2" style={{ minWidth: 130 }}>
                    Renew Membership
                  </button>
                </Link>
              </div>

              {/* Events Section */}
              <div className="container mt-1">
                <div className="text-center mb-5">
                  <h2 className="text-3xl font-bold text-indigo-700">🎉 Upcoming Events</h2>
                  <p className="text-gray-600 mt-2">Stay informed and never miss an opportunity!</p>
                </div>

                <div className="row">
                  {error ? ( // Display error if fetching failed
                    <div className="col-12 text-center text-danger">
                      <p>{error}</p>
                    </div>
                  ) : eventData.length > 0 ? (
                    eventData.map((event) => (
                      <div className="col-md-6 mb-4" key={event.id}>
                        <div className="card h-100 shadow-sm rounded-lg"> {/* Added rounded corners */}
                          <div className="card-header text-black p-3 rounded-top-lg"> {/* Changed text-blue to text-white for better contrast, added padding */}
                            <h5 className="mb-0 font-bold">{event.name || 'Unnamed Event'}</h5> {/* Bold event name */}
                          </div>
                          <div className="card-body d-flex flex-column">
                            <h6 className="card-subtitle mb-2 text-muted">{event.title || 'Special title treatment'}</h6> {/* Changed to subtitle for hierarchy */}
                            <p className="card-text flex-grow-1 text-gray-700"> {/* Added text color */}
                              {event.description || 'Details coming soon.'}
                            </p>
                            <p className="card-text mt-2">
                              <small className="text-muted">
                                Date: <strong>{formatReadableDateRange(event.startdate, event.enddate)}</strong>
                              </small>
                            </p>
                            <Link
                              to={`/eventregistration?id=${event.id}`}
                              className="btn btn-info mt-auto"
                            >
                              Register
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-12 text-center text-muted">
                      <p>No upcoming events at the moment that allow registration.</p> {/* More specific message */}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;